import React, { useState, useEffect, useCallback } from "react";
import { Briefcase, Plus, Pencil, Archive, Trash2, ArchiveRestore, Target, Coins, ClipboardList, FileText, GraduationCap, Users, Info } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

export default function HRPositions() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/positions", { params: { include_archived: showArchived } }); setRows(data.positions); }
    catch { toast.error("Gagal memuat jabatan"); } finally { setLoading(false); }
  }, [showArchived, toast]);
  useEffect(() => { load(); }, [load]);

  const archive = async (p) => {
    try { await api.post(`/hr/positions/${p.id}/archive`, null, { params: { archived: !p.archived } }); toast.success(p.archived ? "Jabatan diaktifkan" : "Jabatan diarsipkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Hapus jabatan "${p.name}" permanen? (hanya Implementation Mode)`)) return;
    try { await api.delete(`/hr/positions/${p.id}`); toast.success("Jabatan dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="hr-positions-page">
      <PageHeader title="Master Jabatan" subtitle="Jabatan menentukan KPI, Payroll, Jobdesk, SOP & Training — System Role hanya mengatur akses" icon={Briefcase}>
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="hr-pos-show-archived" /> Tampilkan arsip
        </label>
        {canManage && <Button onClick={() => setModal({ mode: "create", data: { name: "", department: "", description: "" } })} data-testid="hr-pos-create-btn"><Plus size={15} /> Tambah Jabatan</Button>}
      </PageHeader>

      <div className="mb-4 flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800" data-testid="hr-pos-note">
        <Info size={14} className="mt-0.5 shrink-0" />
        Membuat jabatan baru otomatis membuat Template KPI, Aturan Payroll & Jobdesk (dapat diedit). Karyawan tanpa jabatan otomatis menjadi <b>General Staff</b>.
      </div>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <Card className="overflow-x-auto" data-testid="hr-positions-table">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <tr>
                <th className="py-2 pr-3">Jabatan</th><th className="py-2 pr-3">Departemen</th>
                <th className="py-2 pr-3 text-center"><Users size={13} className="inline" /></th>
                <th className="py-2 pr-3 text-center">KPI</th><th className="py-2 pr-3 text-center">Payroll</th>
                <th className="py-2 pr-3 text-center">Jobdesk</th><th className="py-2 pr-3 text-center">SOP</th>
                <th className="py-2 pr-3 text-center">Training</th>{canManage && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((p) => (
                <tr key={p.id} className={p.archived ? "opacity-50" : ""} data-testid={`hr-pos-row-${p.name}`}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{p.name} {p.archived && <Badge tone="mute">Arsip</Badge>}</td>
                  <td className="py-2.5 pr-3 text-ink-soft">{p.department || "—"}</td>
                  <td className="py-2.5 pr-3 text-center text-ink-soft">{p.employee_count}</td>
                  <td className="py-2.5 pr-3 text-center"><Target size={14} className={p.has_kpi ? "inline text-emerald-600" : "inline text-ink-mute"} /></td>
                  <td className="py-2.5 pr-3 text-center"><Coins size={14} className={p.has_payroll ? "inline text-emerald-600" : "inline text-ink-mute"} /></td>
                  <td className="py-2.5 pr-3 text-center"><ClipboardList size={14} className={p.has_jobdesk ? "inline text-emerald-600" : "inline text-ink-mute"} /></td>
                  <td className="py-2.5 pr-3 text-center text-ink-soft">{p.sop_count || 0}</td>
                  <td className="py-2.5 pr-3 text-center text-ink-soft">{p.training_count || 0}</td>
                  {canManage && (
                    <td className="py-2.5 pr-3 text-right">
                      <button onClick={() => setModal({ mode: "edit", data: { ...p } })} className="mr-1 rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-pos-edit-${p.name}`}><Pencil size={15} /></button>
                      <button onClick={() => archive(p)} className="mr-1 rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" title={p.archived ? "Aktifkan" : "Arsipkan"} data-testid={`hr-pos-archive-${p.name}`}>{p.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}</button>
                      <button onClick={() => del(p)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-pos-del-${p.name}`}><Trash2 size={15} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <PositionModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} toast={toast} />
    </div>
  );
}

function PositionModal({ modal, onClose, onDone, toast }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(modal?.data ? { ...modal.data } : null); }, [modal]);
  if (!modal || !form) return null;
  const isEdit = modal.mode === "edit";
  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Nama jabatan wajib diisi");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/hr/positions/${form.id}`, { name: form.name, department: form.department, description: form.description });
      else await api.post("/hr/positions", { name: form.name, department: form.department, description: form.description });
      toast.success(isEdit ? "Jabatan diperbarui" : "Jabatan dibuat + template KPI/Payroll/Jobdesk otomatis");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit Jabatan" : "Tambah Jabatan"} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} loading={saving} data-testid="hr-pos-submit">{isEdit ? "Simpan" : "Tambah"}</Button></>}>
      <div className="space-y-3" data-testid="hr-pos-form">
        <Input label="Nama Jabatan" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hr-pos-name" />
        <Input label="Departemen" placeholder="Service / Kitchen / Cashier ..." value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="hr-pos-dept" />
        <Input label="Deskripsi (opsional)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hr-pos-desc" />
        {isEdit && <p className="flex items-start gap-1.5 text-xs text-ink-mute"><Info size={13} className="mt-0.5 shrink-0" /> Mengubah nama akan memperbarui semua karyawan & template terkait.</p>}
      </div>
    </Modal>
  );
}
